/**
  * @class msvcDeviceGeneric
  * @description
  * Generic device-id provider. Publishes mmAppDeviceId to msysDomainClient once a stable
  * per-device identifier has been determined:
  * - node/nwjs: MAC address of the primary network interface (prefers eth0/en0)
  * - web: random UUID generated on first run and persisted via mfs.setItem; stable per
  *        browser profile/storage (cleared if the user wipes site data)
  * - capacitor: **not supported** — wire msvcDeviceMobile (Capacitor entry point with
  *        Coppernic awareness) instead. Wiring Generic on a Capacitor build will publish
  *        the literal string 'not-supported-on-generic' as device_id so the
  *        misconfiguration is loud both client- and server-side.
  *
  * No Capacitor plugin dependencies — safe to bundle in pure web/node/nwjs apps.
  */

import msysAbstractService from 'msysAbstractService'
import mfs from 'msysFS'

export default class msvcDeviceGeneric extends msysAbstractService {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union(['msysLogger', 'msysDomainClient'], dependencies)
    cfg_schema = _.extend({
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Resolves the device id for the current platform and publishes it to msysDomainClient.
  */
  async mmStart(sender, params) {
    super.mmStart(sender, params)
    try {
      const device_id = await this.resolveDeviceId()
      this.publish('msysDomainClient', 'mmAppDeviceId', { device_id })
    } catch (e) {
      this.error('mmStart: resolveDeviceId failed: '+(e?.message || e))
      this.publish('msysDomainClient', 'mmAppDeviceId', { device_id: '' })
    }
  } // mmStart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  async resolveDeviceId() {
    const platform = mfs.platform()
    if(platform === 'capacitor') return await this.resolveCapacitor()
    if(platform === 'web') return this.resolveWeb()
    if(platform === 'nwjs' || platform === 'node') {
      const os = mfs.os()
      if(os === 'mac') return this.resolveDarwin()
      if(os === 'win') return this.resolveWindows()
      return this.resolveLinux()
    }
    this.warn('resolveDeviceId: unsupported platform: '+platform)
    return ''
  } // resolveDeviceId

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  resolveWeb() {
    try {
      const stored = String(mfs.getItem('device_id') ?? '')
      if(stored) return stored
      const c = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto : require('crypto')
      const id = c.randomUUID()
      mfs.setItem('device_id', id)
      return id
    } catch (e) {
      this.error('resolveWeb: '+(e?.message || e))
      return ''
    }
  } // resolveWeb

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Stub — Generic intentionally does not depend on @capacitor/device or any Capacitor plugin
  * so it can be bundled in pure web/node/nwjs apps. Capacitor builds must wire msvcDeviceMobile
  * (or another Capacitor-aware subclass) instead. The 'not-supported-on-generic' marker makes
  * the misconfiguration loud both in the client log and in server-side enrollment records.
  */
  async resolveCapacitor() {
    this.warn('resolveCapacitor: msvcDeviceGeneric does not support capacitor — wire msvcDeviceMobile (or another Capacitor-aware subclass) for Capacitor builds')
    return 'not-supported-on-generic'
  } // resolveCapacitor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  resolveLinux() {
    return this.macFromInterfaces(/^(wwan|eth|ethernet)/i, ['eth0'])
  } // resolveLinux

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  resolveDarwin() {
    return this.macFromInterfaces(/^(en|wwan)/i, ['en0'])
  } // resolveDarwin

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  resolveWindows() {
    return this.macFromInterfaces(/^(ethernet|Local Area Connection|wwan)/i, [])
  } // resolveWindows

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Scans network interfaces matching `name_re`, returns a non-zero MAC. If `preferred` lists
  * interface names, those are returned first when present; otherwise the lowest-sorted match wins.
  */
  macFromInterfaces(name_re, preferred) {
    try {
      const reMac = /^([a-zA-Z0-9]{2}[:-]){5}[a-zA-Z0-9]{2}$/
      const ifaces = require('os').networkInterfaces()
      const macs = {}
      for(const name in ifaces) {
        if(!name_re.test(name)) continue
        const list = ifaces[name]
        if(!Array.isArray(list)) continue
        for(const item of list) {
          if(item?.internal) continue
          let mac = String(item?.mac ?? '')
          if(reMac.test(mac)) {
            mac = mac.replace(/[:-]/g, '').toUpperCase()
            if(mac !== '000000000000') {
              macs[name] = mac
              break // all interface items share the same MAC
            }
          }
        }
      }
      for(const name of preferred) if(macs[name]) return macs[name]
      const names = Object.keys(macs).sort()
      if(names.length > 0) return macs[names[0]]
      this.warn('macFromInterfaces: no non-zero MAC address found')
      return ''
    } catch (e) {
      this.error('macFromInterfaces: networkInterfaces() error: '+(e?.message || e))
      return ''
    }
  } // macFromInterfaces
} // msvcDeviceGeneric
